import classnames from 'classnames2';
import React from 'react';

import { IBaseProps } from '../utils';

import headerStyles from './Header.module.scss';

export type HeaderElementTypes = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'span';
export type HeaderVariantTypes = 'hero' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5';

export interface IHeaderProps extends IBaseProps, React.HTMLAttributes<HTMLElement> {
  /** Changes the HTML element used to display the component. */
  as?: HeaderElementTypes;
  /** Defines the id attribute applied to the header tag */
  id?: string;
  /** Defines the role of the heading tag,
   * use "presentation" when the header should not be included in the page outline
   * or parsed as a header for accessibility reasons
   */
  role?: 'heading' | 'presentation';
  /** Defines the visual style and level of the header tag */
  variant: HeaderVariantTypes;
}

const chooseHtmlElement = (as, variant) => {
  if (as) {
    return as;
  } else if (variant === 'hero') {
    return 'h1';
  } else {
    return variant;
  }
};

const mapElementToAriaLevel = (element) => {
  switch (element) {
    case 'span':
      return null;
    case 'h2':
      return 2;
    case 'h3':
      return 3;
    case 'h4':
      return 4;
    case 'h5':
      return 5;
    default:
    case 'h1':
      return 1;
  }
};

export const Header: React.FC<IHeaderProps> = ({
  as,
  children,
  className,
  dataTest,
  id,
  role,
  variant = 'h1',
  ...rest
}) => {
  const ariaRoll = role === 'presentation' ? 'presentation' : 'heading';

  const HtmlElement = chooseHtmlElement(as, variant);
  const ariaLevel = mapElementToAriaLevel(HtmlElement);
  const headerClasses = classnames(headerStyles.header, className, {
    [headerStyles.hero]: variant === 'hero',
    [headerStyles.header1]: variant === 'h1',
    [headerStyles.header2]: variant === 'h2',
    [headerStyles.header3]: variant === 'h3',
    [headerStyles.header4]: variant === 'h4',
    [headerStyles.header5]: variant === 'h5'
  });

  return (
    <HtmlElement
      className={headerClasses}
      data-test={dataTest}
      id={id}
      role={ariaRoll}
      aria-level={ariaLevel}
      {...rest}
    >
      {children}
    </HtmlElement>
  );
};
